/**
 * El SIGPAC de cada nave (tabla `greenhouse_sigpac_recintos`, CQ1): de lo que
 * devuelve la edge `sigpac-proxy` (acción `recintos_nave`) a las filas que se
 * guardan, y de las filas guardadas al estado que enseña la lista «SIGPAC de las
 * naves» de Ajustes → Empresa.
 *
 * LA REGLA DE «POR CONFIRMAR» (PLAN, CQ1): lo que sale del WMS no se da por
 * bueno solo. Queda por confirmar hasta que el admin lo confirma, y además se
 * AVISA cuando:
 *   - el recinto no es de uso IV (invernaderos): el centroide de la Nave 5 de
 *     Campohermoso cae en un camino, «IM - IMPRODUCTIVOS»;
 *   - la nave pisa más de un recinto: la Nave 1 de Las Norias ocupa 0,81 ha y
 *     el recinto de su centroide tiene 0,47.
 * Un recinto escrito a mano por el admin entra ya confirmado (lo ha escrito él)
 * y sin uso: un uso desconocido no cuenta como «distinto de IV».
 * Una nave de uso IV en un solo recinto es la que «cuadra»: se confirma con un
 * toque (y con «Confirmar las que cuadran», todas a la vez).
 *
 * El cuaderno (CQ2) imprime SOLO los recintos confirmados.
 *
 * LA PROPUESTA AUTOMÁTICA (27-09-2026, petición de Pablo; regla en
 * `./sigpacPropuesta.ts`): lo que hay sin confirmar de una nave se compara en
 * SUPERFICIE con la nave (su dibujo). Un recinto que casa, de uso invernadero (o
 * compatible) y donde cae la nave → «propuesto», y la nave «cuadra»: se confirma de
 * un toque. Si hay dudas → los candidatos, para elegir. La propuesta no se guarda
 * aparte: se deriva de las filas sin confirmar y del dibujo, en la pantalla con esta
 * copia de la regla y en el servidor (alta de la nave, relleno) con la copia Deno.
 *
 * LO ELEGIDO AL DIBUJAR LA NAVE (27-09-2026 por la tarde, Pablo: «que al menos los
 * datos ya estén guardados, y después en Ajustes te pida seleccionar cuáles»): los
 * recintos que el usuario clicó en el diseñador llegan con fuente `disenador`, sin
 * confirmar. Van PRIMERO y marcados por defecto; el resto de candidatos, para marcar
 * cuáles más son de la nave (una nave puede ocupar varios recintos).
 */
import { checkSigpac, formatSigpac, type SigpacCodes } from "./validators";
import { proponerRecinto, type CandidatoSigpac, type PropuestaSigpac } from "./sigpacPropuesta";

/** Un recinto tal y como lo devuelve `sigpac-proxy` con `action: "recintos_nave"`. */
export interface RecintoWms {
  provincia: string;
  municipio: string;
  municipio_nombre?: string | null;
  agregado: string;
  zona: string;
  poligono: string;
  parcela: string;
  recinto: string;
  uso_sigpac?: string | null;
  superficie_ha?: number | null;
  coef_regadio?: number | null;
  muestras?: number | null;
  fuente: "wms_centroide" | "wms_poligono";
}

/** Una fila guardada (lo que se lee de `greenhouse_sigpac_recintos`). */
export interface RecintoGuardado {
  id: string;
  greenhouse_id: string;
  provincia: string;
  municipio_codigo: string;
  municipio_nombre: string | null;
  agregado: string;
  zona: string;
  poligono: string;
  parcela: string;
  recinto: string;
  uso_sigpac: string | null;
  superficie_ha: number | null;
  coef_regadio: number | null;
  fuente: string;
  muestras: number | null;
  confirmado_en: string | null;
}

export type AvisoSigpac = "uso_no_iv" | "varios_recintos";

/** Fuente de las filas que el usuario eligió al dibujar la nave (diseñador). */
export const FUENTE_DISENADOR = "disenador";

export interface EstadoSigpacNave<T extends RecintoPropuestaFila = RecintoPropuestaFila> {
  estado: "sin_sigpac" | "por_confirmar" | "confirmado";
  avisos: AvisoSigpac[];
  /** La propuesta está clara («propuesto»): se confirma de un toque. */
  cuadra: boolean;
  /**
   * La propuesta, si TODO lo de la nave está sin confirmar y salió del SIGPAC
   * (nada confirmado ni escrito a mano). null en otro caso.
   */
  propuesta: PropuestaSigpac<T & CandidatoSigpac> | null;
  /** Lo elegido al dibujar la nave (sin confirmar): va primero. */
  elegidos: T[];
  /** Lo que sale marcado para confirmar: lo elegido al dibujar o, si no, el propuesto. */
  aConfirmar: T[];
}

/** Lo que la regla necesita de una fila guardada. */
export type RecintoPropuestaFila = Pick<RecintoGuardado, "uso_sigpac" | "confirmado_en"> &
  Partial<Pick<RecintoGuardado, "id" | "superficie_ha" | "muestras" | "fuente">>;

export const USO_INVERNADERO = "IV";

/** Uso SIGPAC en su código de dos letras: «IV - INVERNADEROS…» → «IV». */
export function usoCodigo(uso: string | null | undefined): string | null {
  const m = /^\s*([A-Za-z]{2})\b/.exec(uso ?? "");
  return m ? m[1].toUpperCase() : null;
}

/**
 * Estado de una nave para la lista. `areaNaveHa` es la superficie de su dibujo
 * (`areaAnilloHa`); sin ella la propuesta queda «dudosa» (no hay con qué comparar).
 */
export function estadoSigpacNave<T extends RecintoPropuestaFila>(recintos: T[], areaNaveHa: number | null = null): EstadoSigpacNave<T> {
  if (recintos.length === 0) return { estado: "sin_sigpac", avisos: [], cuadra: false, propuesta: null, elegidos: [], aConfirmar: [] };
  const avisos: AvisoSigpac[] = [];
  const confirmado = recintos.every((r) => !!r.confirmado_en);
  const todoDelSigpac = recintos.every((r) => !r.confirmado_en && r.fuente !== "manual");
  const propuesta = todoDelSigpac
    ? proponerRecinto(
        areaNaveHa,
        // Las MISMAS filas (no copias) cuando ya traen superficie y puntos: así el
        // propuesto y los candidatos se comparan por identidad con lo elegido al dibujar.
        recintos.map((r) =>
          r.superficie_ha !== undefined && r.muestras !== undefined
            ? (r as T & CandidatoSigpac)
            : ({ ...r, superficie_ha: r.superficie_ha ?? null, muestras: r.muestras ?? null } as T & CandidatoSigpac),
        ),
      )
    : null;
  const elegidos = todoDelSigpac ? recintos.filter((r) => r.fuente === FUENTE_DISENADOR) : [];
  const propuesto = (propuesta?.propuesto ?? null) as T | null;
  const aConfirmar = elegidos.length > 0 ? elegidos : propuesto ? [propuesto] : [];
  // Con propuesta, los avisos miran solo a lo que se va a confirmar (el resto son candidatos, no «suyos»).
  const miran = propuesta ? aConfirmar : recintos;
  // Un uso DESCONOCIDO (recinto escrito a mano) no es «distinto de IV»: no se avisa de lo que no se sabe.
  if (miran.some((r) => usoCodigo(r.uso_sigpac) != null && usoCodigo(r.uso_sigpac) !== USO_INVERNADERO)) avisos.push("uso_no_iv");
  if (!propuesta && recintos.length > 1) avisos.push("varios_recintos");
  // «Cuadra» (se confirma de un toque, también con «Confirmar las que cuadran»): lo
  // elegido al dibujar, si la regla no propone OTRO recinto; o, sin elegidos, la propuesta clara.
  const cuadra =
    !confirmado &&
    (elegidos.length > 0 ? !propuesto || elegidos.includes(propuesto) : propuesta?.estado === "propuesto");
  return {
    estado: confirmado ? "confirmado" : "por_confirmar",
    avisos,
    cuadra,
    propuesta,
    elegidos,
    aConfirmar,
  };
}

/** Lo que se inserta para cada recinto derivado (sin confirmar). null si los códigos no valen. */
export function filaDesdeWms(greenhouseId: string, companyId: string, r: RecintoWms) {
  const chk = checkSigpac({
    provincia: r.provincia,
    municipio: r.municipio,
    agregado: r.agregado,
    zona: r.zona,
    poligono: r.poligono,
    parcela: r.parcela,
    recinto: r.recinto,
  });
  if (!chk.ok) return null;
  const c = chk.codes;
  return {
    company_id: companyId,
    greenhouse_id: greenhouseId,
    provincia: c.provincia,
    municipio_codigo: c.municipio,
    municipio_nombre: r.municipio_nombre?.trim() || null,
    agregado: c.agregado,
    zona: c.zona,
    poligono: c.poligono,
    parcela: c.parcela,
    recinto: c.recinto,
    uso_sigpac: usoCodigo(r.uso_sigpac),
    superficie_ha: r.superficie_ha != null && Number.isFinite(r.superficie_ha) ? Number(r.superficie_ha) : null,
    coef_regadio: r.coef_regadio != null && Number.isFinite(r.coef_regadio) ? Number(r.coef_regadio) : null,
    fuente: r.fuente,
    muestras: r.muestras ?? null,
    confirmado_por: null,
    confirmado_en: null,
  };
}

export const codigosDe = (r: Pick<RecintoGuardado, "provincia" | "municipio_codigo" | "agregado" | "zona" | "poligono" | "parcela" | "recinto">): SigpacCodes => ({
  provincia: r.provincia,
  municipio: r.municipio_codigo,
  agregado: r.agregado,
  zona: r.zona,
  poligono: r.poligono,
  parcela: r.parcela,
  recinto: r.recinto,
});

/** «04-102-0-0-011-00382-00009» de una fila guardada. */
export const referenciaSigpac = (r: Parameters<typeof codigosDe>[0]) => formatSigpac(codigosDe(r));

/**
 * El polígono de la nave en WGS84 ([lng, lat]) desde `greenhouses.geometry`, o
 * null. El diseñador lo guarda en `geometry.geoPolygonWgs84` como un Polygon de
 * GeoJSON; se acepta también el anillo suelto.
 */
export function anilloWgs84(geometry: unknown): [number, number][] | null {
  const geo = (geometry as { geoPolygonWgs84?: unknown } | null)?.geoPolygonWgs84 as
    | { type?: string; coordinates?: unknown }
    | unknown[]
    | null
    | undefined;
  const ring = Array.isArray(geo) ? geo : Array.isArray((geo as { coordinates?: unknown[] })?.coordinates) ? (geo as { coordinates: unknown[] }).coordinates[0] : null;
  if (!Array.isArray(ring)) return null;
  const pts = ring.filter(
    (p): p is [number, number] => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]),
  ).map((p) => [Number(p[0]), Number(p[1])] as [number, number]);
  return pts.length >= 3 ? pts : null;
}

/**
 * Dónde se pregunta al WMS por una nave: el centroide guardado y, si no hay, el
 * medio del anillo. null si la nave no tiene ni lo uno ni lo otro (alta a mano sin
 * dibujo): entonces solo cabe escribir el SIGPAC a mano.
 */
export function consultaDeNave(nave: { centroid_lat?: number | null; centroid_lng?: number | null; geometry?: unknown }):
  | { centroid: { lat: number; lng: number } | null; polygon: [number, number][] | null }
  | null {
  const polygon = anilloWgs84(nave.geometry);
  const lat = Number(nave.centroid_lat);
  const lng = Number(nave.centroid_lng);
  let centroid = nave.centroid_lat != null && nave.centroid_lng != null && Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  if (!centroid && polygon) {
    // Sin el último vértice si cierra el anillo, para no pesar dos veces el primero.
    const pts = polygon.length > 3 && polygon[0][0] === polygon[polygon.length - 1][0] && polygon[0][1] === polygon[polygon.length - 1][1] ? polygon.slice(0, -1) : polygon;
    centroid = { lng: pts.reduce((s, p) => s + p[0], 0) / pts.length, lat: pts.reduce((s, p) => s + p[1], 0) / pts.length };
  }
  if (!centroid && !polygon) return null;
  return { centroid, polygon };
}

/** Lo que el SIGPAC dijo de un recinto escrito a mano al comprobarlo (`recinto_por_codigo`). */
export interface DatosRecintoSigpac {
  municipio_nombre?: string | null;
  uso_sigpac?: string | null;
  superficie_ha?: number | null;
  coef_regadio?: number | null;
}

const numero = (n: unknown) => (n != null && Number.isFinite(Number(n)) ? Number(n) : null);

/**
 * Un recinto escrito a mano por el admin: entra confirmado. Sin uso ni superficie
 * si no se comprobó en el SIGPAC (no se inventan); con lo que dijo el SIGPAC si se
 * comprobó (24-10-2026: antes entraba siempre vacío y el cuaderno lo imprimía sin
 * uso ni superficie, y la zona de plagas caía a la provincia por no tener municipio).
 */
export function filaManual(greenhouseId: string, companyId: string, codes: SigpacCodes, userId: string | null, ahora = new Date(), datos: DatosRecintoSigpac = {}) {
  return {
    company_id: companyId,
    greenhouse_id: greenhouseId,
    provincia: codes.provincia,
    municipio_codigo: codes.municipio,
    municipio_nombre: datos.municipio_nombre?.trim() || null,
    agregado: codes.agregado,
    zona: codes.zona,
    poligono: codes.poligono,
    parcela: codes.parcela,
    recinto: codes.recinto,
    uso_sigpac: usoCodigo(datos.uso_sigpac),
    superficie_ha: numero(datos.superficie_ha),
    coef_regadio: numero(datos.coef_regadio),
    fuente: "manual" as const,
    muestras: null,
    confirmado_por: userId,
    confirmado_en: ahora.toISOString(),
  };
}

/** Las columnas que hacen único un recinto de una nave (el `onConflict` de la tabla). */
export const CONFLICTO_RECINTO = "greenhouse_id,provincia,municipio_codigo,agregado,zona,poligono,parcela,recinto";

/* El cliente de Supabase (el del navegador, o uno de mentira en las pruebas). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Db = any;

/**
 * Escribir A MANO el recinto de una nave: lo que la nave tenía SIN confirmar se
 * quita (lo confirmado se queda) y el recinto entra confirmado a nombre de quien
 * lo dijo (`filaManual`). Lo usan «Escribir a mano» de Ajustes → Empresa
 * (useSigpacNaves) y la tarjeta del asistente del cuaderno (CU7): una sola forma
 * de guardarlo. Solo escribe el admin (RLS); devuelve el error de la base tal
 * cual (quien llama lo relanza sin valores, E11).
 */
export async function escribirRecintoManual(
  db: Db,
  o: { companyId: string; naveId: string; codes: SigpacCodes; userId: string | null; ahora?: Date; datos?: DatosRecintoSigpac },
): Promise<{ error: { code?: string; message?: string } | null }> {
  /* Si lo escrito YA es un candidato de la nave (24-10-2026), se confirma ese,
     con su uso, superficie y municipio, en vez de borrarlo y meterlo vacío. Los
     demás sin confirmar se quitan igual que siempre. */
  const ya = await db
    .from("greenhouse_sigpac_recintos")
    .select("id, uso_sigpac, superficie_ha, municipio_nombre, coef_regadio")
    .eq("greenhouse_id", o.naveId)
    .eq("provincia", o.codes.provincia)
    .eq("municipio_codigo", o.codes.municipio)
    .eq("agregado", o.codes.agregado)
    .eq("zona", o.codes.zona)
    .eq("poligono", o.codes.poligono)
    .eq("parcela", o.codes.parcela)
    .eq("recinto", o.codes.recinto)
    .is("confirmado_en", null)
    .limit(1);
  const candidato = !ya.error && Array.isArray(ya.data) ? (ya.data[0] as Partial<RecintoGuardado> | undefined) : undefined;
  if (candidato?.id) {
    const quitar = await db.from("greenhouse_sigpac_recintos").delete().eq("greenhouse_id", o.naveId).is("confirmado_en", null).neq("id", candidato.id);
    if (quitar.error) return { error: quitar.error };
    const d = o.datos ?? {};
    const { error } = await db
      .from("greenhouse_sigpac_recintos")
      .update({
        confirmado_en: (o.ahora ?? new Date()).toISOString(),
        confirmado_por: o.userId,
        uso_sigpac: candidato.uso_sigpac ?? usoCodigo(d.uso_sigpac),
        superficie_ha: candidato.superficie_ha ?? numero(d.superficie_ha),
        municipio_nombre: candidato.municipio_nombre ?? (d.municipio_nombre?.trim() || null),
        coef_regadio: candidato.coef_regadio ?? numero(d.coef_regadio),
      })
      .eq("id", candidato.id);
    return { error: error ?? null };
  }
  const quitar = await db.from("greenhouse_sigpac_recintos").delete().eq("greenhouse_id", o.naveId).is("confirmado_en", null);
  if (quitar.error) return { error: quitar.error };
  const { error } = await db
    .from("greenhouse_sigpac_recintos")
    .upsert(filaManual(o.naveId, o.companyId, o.codes, o.userId, o.ahora, o.datos), { onConflict: CONFLICTO_RECINTO });
  return { error: error ?? null };
}
